import { Injectable } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { doc, Firestore, getDoc } from '@angular/fire/firestore';
import { from, Observable } from 'rxjs';
import { UserService } from '../../../../../chat-common/src/lib/services/user.service';
import { BcraIneligibleReason, BcraReport, BcraRiskEstimate, BcraRiskKind, BreastDensity, riskChartScale } from './bcra-report.model';

@Injectable({ providedIn: 'root' })
export class BcraReportService {
  constructor(
    private fs: Firestore,
    private auth: Auth,
    private userService: UserService,
  ) {}

  getReport(): Observable<BcraReport> {
    return from(this.loadReport());
  }

  private async loadReport(): Promise<BcraReport> {
    // On a fresh page load Firebase Auth restores the session asynchronously
    await this.auth.authStateReady();

    const uid = this.userService.user?.id;
    if (!uid) {
      throw new Error('No user id available to load the BCRA report');
    }
    const quizPath = ['v0', 'data', 'user', uid, 'quiz', 'quiz-bcrisk'] as const;
    const [snap, answersSnap] = await Promise.all([
      getDoc(doc(this.fs, ...quizPath, 'results-gail', 'current')),
      getDoc(doc(this.fs, ...quizPath, 'answers', 'current')),
    ]);
    const answers = answersSnap.exists() ? answersSnap.data() : null;
    if (!snap.exists()) {
      // No results yet: the survey is either in progress or not started
      const progress = await getDoc(doc(this.fs, ...quizPath, 'temp-progress', 'current'));
      if (progress.exists()) return { status: 'incomplete', result: null };
      if (!answers) return { status: 'not-started', result: null };
    }

    const data = snap.exists() ? snap.data() : null;
    // Newer calculations store the 5-year and lifetime responses separately; older ones only have responseData
    const fiveYear = parseJson(data?.['responseDataFiveYear']);
    const lifetime = parseJson(data?.['responseDataLifetime']);
    const responses: [BcraRiskKind, any][] = fiveYear && lifetime
      ? [['five-year', fiveYear], ['lifetime', lifetime]]
      : [['five-year', fiveYear ?? parseJson(data?.['responseData'])]];

    // Gail isn't valid for these people, so no estimate is shown even if one was calculated
    const ineligibleReason = getIneligibleReason(answers, toSummary(responses[0][1]?.summary)?.T1);
    if (ineligibleReason) {
      return { status: 'ineligible', result: null, ineligibleReason };
    }
    if (!data) {
      return { status: 'not-started', result: null };
    }

    const estimates = responses.map(([kind, response]) => toEstimate(kind, response));
    if (data['verified'] !== true || estimates.some((e) => !e)) {
      // Answers are submitted but the result isn't verified or calculated yet
      return { status: 'pending', result: null };
    }

    const request = parseJson(data['requestData']);
    return {
      status: 'ready',
      result: {
        estimates: estimates as BcraRiskEstimate[],
        breastDensity: toBreastDensity(request?.personalHealthHistory?.breastDensity),
        answeredAt: toDate(request?.createdAt),
      },
    };
  }
}

/** Picks the single reason to show when several apply, in priority order. */
function getIneligibleReason(answers: any, age: unknown): BcraIneligibleReason | null {
  const history = parseJson(answers?.personalHealthHistory);
  if (matchesAny(history?.brcaTestResult, ['positiveBrca1', 'positiveBrca2'])) return 'brca';
  if (matchesAny(history?.breastBiopsyResult, ['lcis', 'dcis'])) return 'in-situ';
  // Age at assessment, as the Gail calculation saw it
  const ageAtAssessment = parseFloat(age as string);
  if (!isNaN(ageAtAssessment) && ageAtAssessment < 35) return 'under-35';
  if (matchesAny(history?.hasChestRadiationForHodgkinLymphoma, ['yes'])) return 'hodgkin';
  return null;
}

/** Answers can be a single value or a list; compared case-insensitively. */
function matchesAny(value: unknown, expected: string[]): boolean {
  const values = Array.isArray(value) ? value : [value];
  const wanted = expected.map((v) => v.toLowerCase());
  return values.some((v) => typeof v === 'string' && wanted.includes(v.toLowerCase()));
}

/** Null while the response is missing, has errors or has no numbers yet. */
function toEstimate(kind: BcraRiskKind, response: any): BcraRiskEstimate | null {
  const summary = toSummary(response?.summary);
  const risk = parseFloat(summary?.AbsRisk);
  const averageRisk = parseFloat(summary?.AbsRisk_Avg);
  if (response?.errors?.length || isNaN(risk) || isNaN(averageRisk)) return null;
  return { kind, risk, averageRisk, scale: riskChartScale(kind, [risk, averageRisk]) };
}

/** The Gail response holds one summary row, as a list or a single object. */
function toSummary(summary: unknown): any {
  return Array.isArray(summary) ? summary[0] ?? null : summary ?? null;
}

/** Firestore may hold the payloads as JSON strings or as maps. */
function parseJson(value: unknown): any {
  if (typeof value !== 'string') return value ?? null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function toDate(value: unknown): Date | null {
  if (typeof value !== 'string') return null;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

/** BI-RADS categories: a/b are not dense, c/d are dense. */
function toBreastDensity(category: unknown): BreastDensity {
  switch (typeof category === 'string' ? category.toLowerCase() : null) {
    case 'a':
    case 'b':
      return 'Not Dense';
    case 'c':
    case 'd':
      return 'Dense';
    default:
      return 'Unknown';
  }
}
